import { IFluidObject } from '@fluidframework/core-interfaces';
import { ComponentFocusable } from '@fluidx/office-fluid-types';
import { FindIframe } from '../Shared/FindIframe';
import { PostMessageSender, PostMessageHandlers } from '../Shared/PostMessage';
import {
  GiveFocusMessage,
  GiveHostFocusCalledMessage,
  SetGiveHostFocusMessage,
  GiveFocusReplyMessage,
  FocusDirection
} from '../MessageTypes';

/**
 * Initializes everything needed to handle the Focusable interface outside the
 * iframe.
 * @param sender how to send messages to the inside of the iframe
 * @returns A dummy component with just this interface and the set of message
 * handlers this interface is expecting
 */
export function getHostSideFocusable(
  sender: PostMessageSender,
  findIframe: FindIframe
): { component: ComponentFocusable; handlers: PostMessageHandlers<string> } {
  let hostComponent: ComponentFocusable | undefined;
  const component: ComponentFocusable = {
    get ComponentFocusable(): ComponentFocusable {
      return this;
    },
    giveFocus: (focusDirection?: FocusDirection): boolean => {
      const message: GiveFocusMessage = {
        type: 'GiveFocus',
        payload: { focusDirection }
      };
      sender.sendWithReturn(message, /*timeout*/ true).catch(() => {
        // If we have a way to give focus back to the host, do so
        if (hostComponent) {
          hostComponent.giveFocus();
        }

        // If all else fails, at least get focus to the iframe
        findIframe()?.focus();
      });
      return true;
    },
    setHostComponentFocusable(componentToSet: ComponentFocusable) {
      hostComponent = componentToSet;
      const message: SetGiveHostFocusMessage = {
        type: 'SetGiveHostFocus',
        payload: { hostReferenceDefined: hostComponent !== undefined }
      };
      sender.send(message); // TODO: what happens if this fails?
    },
    isComponentFocused() {
      const element = findIframe();
      return element ? element.contains(document.activeElement) : false;
    }
  };
  const handlers: PostMessageHandlers<string> = {
    ['GiveHostFocusCalled']: (_message) => {
      if (!hostComponent?.ComponentFocusable?.giveFocus()) {
        findIframe()?.focus();
      }
    }
  };
  return { component, handlers };
}

/**
 * Initializes everything needed to handle the Focusable interface inside the
 * iframe.
 * @param component the actual component with the real implementation of the interface
 * @param sender how to send messages to the outside of the iframe
 */
export function getGuestSideFocusable(
  component: IFluidObject,
  sender: PostMessageSender
): PostMessageHandlers<string> | undefined {
  const componentFocusable = component.ComponentFocusable;

  if (!componentFocusable) {
    return undefined;
  }

  // Listen for escape to exit component if escape has not been handles by iframe contents
  const escapeKeyCode = 27;
  document.documentElement.addEventListener('keydown', (event: KeyboardEvent) => {
    if (event.keyCode === escapeKeyCode) {
      window.setTimeout(() => {
        if (!event.defaultPrevented) {
          const message: GiveHostFocusCalledMessage = {
            type: 'GiveHostFocusCalled'
          };
          sender.send(message);
        }
      }, 0);
    }
  });

  return {
    ['GiveFocus']: (message) => {
      let { focusDirection } = { ...(message as GiveFocusMessage).payload };
      const result = componentFocusable.giveFocus(focusDirection);
      const reply: GiveFocusReplyMessage = {
        type: 'GiveFocusReply',
        payload: { result }
      };
      sender.sendReply(message, reply);
    },
    ['SetGiveHostFocus']: (message) => {
      let { hostReferenceDefined } = { ...(message as SetGiveHostFocusMessage).payload };
      // TODO: This check is for backwards compatibility and should be removed when we are confident that all hosts have an updated version of the proxy
      if (hostReferenceDefined === undefined) {
        const { callbackDefined } = { ...(message as SetGiveHostFocusMessage).payload };
        hostReferenceDefined = callbackDefined;
      }
      if (hostReferenceDefined) {
        if (componentFocusable.setHostComponentFocusable) {
          // If the host provided us a way to call its giveFocus in order to give it back focus, we construct a host componentFocusable on the guest side that will send a message to the host side of the proxy whe giveFocus is called
          const hostComponent: ComponentFocusable = {
            get ComponentFocusable(): ComponentFocusable {
              return this;
            },
            giveFocus: () => {
              const message: GiveHostFocusCalledMessage = {
                type: 'GiveHostFocusCalled'
              };
              sender.send(message);
              return true;
            },
            isComponentFocused: () => {
              return true;
            }
          };
          // Update real componentFocusable inside the iframe with a host component reference
          componentFocusable.setHostComponentFocusable(hostComponent);
        }
      }
    }
  };
}
